/**
 * New WhatsApp template (/admin/communications/whatsapp/templates/new?message=61000): the assisted creator.
 *
 * Step by step: 1 Message (its fields become chips), 2 Category (with guidance), 3 Name and language (auto name in
 * lower_snake_case), 4 Wording (optional header, the body with field chips that become {{1}}.. in order, a sample per
 * variable, optional footer, buttons: link with a changing part, quick replies, call), 5 Check and submit. A phone
 * preview and the rule checker follow every change. "Use suggested wording" fills CCMS's starter for the message;
 * "Suggest wording" asks Claude (AI flag WhatsAppTemplateWording; hidden with an admin note until it is on).
 * "Submit to Meta" goes through 360dialog: the template shows as Pending and links itself to its message once Approved.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  IconArrowLeft,
  IconSparkles,
  IconWand,
  IconPlus,
  IconTrash,
  IconCircleCheck,
  IconAlertTriangle,
  IconSend,
  IconBulb,
  IconLock,
  IconExternalLink,
  IconArrowBackUp,
  IconPhone,
} from '@tabler/icons-react';
import { LayoutContainer } from '@/components/ui/layout';
import {
  Stack,
  Group,
  Title,
  Text,
  Button,
  Badge,
  Paper,
  Select,
  TextInput,
  Textarea,
  Switch,
  Alert,
  Divider,
  Stepper,
  SimpleGrid,
  Chip,
  ActionIcon,
  Tooltip,
  Card,
  List,
  Loader,
} from '@/components/ui';
import { FadeIn } from '@/components/animations/transitions';
import { PageHelpButton } from '@/components/help';
import { toast } from '@/components/ui/providers/AlertProvider';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { useWhatsAppTemplateService } from '@actschurch/ccms-sdk';
import type { WhatsAppTemplateDraft, WhatsAppTemplateDraftButton, WhatsAppMessageDescriptor, WhatsAppMessageField } from '@actschurch/ccms-sdk';
import { WhatsAppPhonePreview } from './WhatsAppPhonePreview';
import { checkDraft, previewDraft, variablesOf, normaliseName, isValidName, BODY_MAX, HEADER_MAX, FOOTER_MAX, BUTTON_TEXT_MAX } from './whatsappTemplateRules';
import { useWhatsAppMessages, useWhatsAppTemplateAi, whatsAppErrorText, whatsAppTemplateKeys } from './useWhatsAppTemplates';
import { useQueryClient } from '@tanstack/react-query';

export const WHATSAPP_TEMPLATE_CREATOR_PAGE = 'communications.whatsapp-template-creator';
const PAGE = WHATSAPP_TEMPLATE_CREATOR_PAGE;
const BACK = '/admin/communications/whatsapp/templates';

const LANGUAGES = [
  { value: 'en', label: 'English (en)' },
  { value: 'en_GB', label: 'English, UK (en_GB)' },
  { value: 'en_US', label: 'English, US (en_US)' },
  { value: 'af', label: 'Afrikaans (af)' },
  { value: 'zu', label: 'Zulu (zu)' },
  { value: 'xh', label: 'Xhosa (xh)' },
];

const CATEGORIES = [
  {
    code: 'UTILITY',
    name: 'Utility',
    when: 'Confirmations and updates about something the person did or asked for: an entry received, a booking approved, a payment due.',
    note: 'Recommended for CCMS messages. No promotion of any kind.',
    color: 'blue',
  },
  {
    code: 'MARKETING',
    name: 'Marketing',
    when: 'Invitations, announcements and promotions: an event coming up, a new course, a campaign.',
    note: 'Only for people who agreed to receive them (opt-in). Meta charges more for them.',
    color: 'grape',
  },
  {
    code: 'AUTHENTICATION',
    name: 'Authentication',
    when: "One-time codes in Meta's fixed format (code and a copy button).",
    note: 'CCMS sends sign-in codes by SMS and email only, so this cannot be submitted here.',
    color: 'orange',
    disabled: true,
  },
];

const emptyDraft = (): WhatsAppTemplateDraft => ({ templateId: null, name: '', category: 'UTILITY', languageCode: 'en', headerText: '', bodyText: '', footerText: '', buttons: [], variables: [] });

/** Renumbers the body's fields {{1}}.. in order of first use and keeps each one's field and sample. */
function renumber(d: WhatsAppTemplateDraft): WhatsAppTemplateDraft {
  const order: number[] = [];
  variablesOf(d.bodyText).forEach((v) => v.number !== null && !order.includes(v.number) && order.push(v.number));
  const map = new Map(order.map((n, i) => [n, i + 1]));
  const body = d.bodyText.replace(/\{\{\s*(\d+)\s*\}\}/g, (_m, n) => `{{${map.get(parseInt(n, 10)) ?? n}}}`);
  const variables = [
    ...d.variables.filter((v) => v.component === 'header'),
    ...order.map((old, i) => {
      const was = d.variables.find((v) => v.component === 'body' && v.index === old);
      return { component: 'body', index: i + 1, field: was?.field ?? '', sample: was?.sample ?? '' };
    }),
  ];
  return { ...d, bodyText: body, variables };
}

function ButtonIcon({ type }: { type: string }) {
  return type === 'URL' ? <IconExternalLink size={14} /> : type === 'PHONE_NUMBER' ? <IconPhone size={14} /> : <IconArrowBackUp size={14} />;
}

/** "What a good WhatsApp message includes" (the creator's guidance panel). */
function GoodMessagePanel() {
  return (
    <Paper withBorder radius="md" p="md" data-help-id={`${PAGE}.guidance`}>
      <Group gap="xs" mb="xs">
        <IconBulb size={18} />
        <Text fw={600} size="sm">
          What a good WhatsApp message includes
        </Text>
      </Group>
      <List
        size="sm"
        spacing={4}
        items={[
          'The person\'s name first: "Hi Thandi,".',
          'What happened, in the first sentence: their entry was received, a place opened up.',
          'The one or two details that identify it: the form or event, a reference, a date or an amount.',
          'What happens next or what they can do, with a button for it (view, confirm, pay).',
          'One to three short sentences. Warm, plain South African English, no long dashes, at most one emoji.',
          'Nothing secret: never codes, passwords or reset links (those go by SMS and email).',
        ]}
      />
    </Paper>
  );
}

export default function WhatsAppTemplateCreatorPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const service = useWhatsAppTemplateService();
  const { hasRole } = useEnrichedAuth();
  const isAdmin = hasRole('Admin');
  const messages = useWhatsAppMessages('communications');
  const ai = useWhatsAppTemplateAi();

  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<WhatsAppTemplateDraft>(emptyDraft);
  const [nameTouched, setNameTouched] = useState(false);
  const [linkToMessage, setLinkToMessage] = useState(true);
  const [replaceLink, setReplaceLink] = useState(false);
  const [instructions, setInstructions] = useState('');
  const [aiNotes, setAiNotes] = useState<string[]>([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const all = messages.data ?? [];
  const message: WhatsAppMessageDescriptor | null = all.find((m) => m.templateId === draft.templateId) ?? null;
  const fields: WhatsAppMessageField[] = message?.fields ?? [];

  // ?step=1..5 opens a step (the page's tour uses it); the draft stays as it is.
  useEffect(() => {
    const s = Number(params.get('step'));
    if (s >= 1 && s <= 5) setStep(s - 1);
  }, [params]);

  // ?message=61000 picks the message once the catalogue is loaded.
  useEffect(() => {
    const id = Number(params.get('message'));
    if (id && all.length > 0 && !draft.templateId) pickMessage(id);
  }, [all.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (patch: Partial<WhatsAppTemplateDraft>) => setDraft((d) => ({ ...d, ...patch }));
  const check = useMemo(() => checkDraft(draft), [draft]);
  const preview = useMemo(() => previewDraft(draft), [draft]);
  const errorsFor = (...parts: string[]) => check.errors.filter((e) => parts.includes(e.part));

  function pickMessage(id: number | null) {
    const m = all.find((x) => x.templateId === id) ?? null;
    setDraft((d) => ({ ...d, templateId: id, name: nameTouched && d.name ? d.name : m?.suggestedTemplateName ?? d.name }));
  }

  const applyStarter = () => {
    if (!message?.starter) return;
    setDraft({ ...message.starter, templateId: message.templateId, name: draft.name || message.starter.name, languageCode: draft.languageCode || 'en', headerText: '', footerText: '' });
    setAiNotes([]);
    toast.success('Suggested wording added', 'Check it, then go to Check and submit.');
  };

  /** Insert a field at the cursor: an existing field reuses its number, a new one gets the next. */
  const insertField = (f: WhatsAppMessageField) => {
    const existing = draft.variables.find((v) => v.component === 'body' && v.field === f.key);
    const index = existing?.index ?? Math.max(0, ...draft.variables.filter((v) => v.component === 'body').map((v) => v.index)) + 1;
    const token = `{{${index}}}`;
    const el = bodyRef.current;
    const at = el ? el.selectionStart : draft.bodyText.length;
    const end = el ? el.selectionEnd : draft.bodyText.length;
    const before = draft.bodyText.slice(0, at);
    const pad = before.length > 0 && !/\s$/.test(before) ? ' ' : '';
    const body = before + pad + token + draft.bodyText.slice(end);
    const variables = existing ? draft.variables : [...draft.variables, { component: 'body', index, field: f.key, sample: f.sample || f.label }];
    setDraft(renumber({ ...draft, bodyText: body, variables }));
    requestAnimationFrame(() => el?.focus());
  };

  const onBodyChange = (text: string) => {
    // Drop variables whose {{n}} is gone; keep the rest as they are (Renumber fixes gaps).
    const present = new Set(variablesOf(text).map((v) => v.number));
    setDraft((d) => ({ ...d, bodyText: text, variables: d.variables.filter((v) => v.component !== 'body' || present.has(v.index)) }));
  };

  const setVariable = (component: string, index: number, patch: { field?: string; sample?: string }) =>
    setDraft((d) => {
      const has = d.variables.some((v) => v.component === component && v.index === index);
      return {
        ...d,
        variables: has
          ? d.variables.map((v) => (v.component === component && v.index === index ? { ...v, ...patch } : v))
          : [...d.variables, { component, index, field: patch.field ?? '', sample: patch.sample ?? '' }],
      };
    });

  const setButton = (i: number, patch: Partial<WhatsAppTemplateDraftButton>) => set({ buttons: draft.buttons.map((b, j) => (j === i ? { ...b, ...patch } : b)) });
  const addButton = (type: string) => {
    const link = fields.find((f) => f.isLink);
    const base = link?.sample ? link.sample.replace(/[^/]*$/, '') : 'https://ccms.actschurch.co.za/';
    set({
      buttons: [
        ...draft.buttons,
        type === 'URL'
          ? { type, text: '', url: link ? `${base}{{1}}` : 'https://', field: link?.key ?? null, sample: link?.sample ?? null }
          : type === 'PHONE_NUMBER'
            ? { type, text: 'Call us', phoneNumber: '+27 ' }
            : { type, text: '' },
      ],
    });
  };

  const suggest = async () => {
    setAiBusy(true);
    setAiError(null);
    try {
      const s = await service.suggestWording({ templateId: draft.templateId ?? null, draft, instructions: instructions || null });
      const samples = new Map(fields.map((f) => [f.key, f.sample || f.label]));
      setDraft((d) => ({
        ...d,
        headerText: s.headerText ?? '',
        bodyText: s.bodyText,
        footerText: s.footerText ?? '',
        variables: [
          ...d.variables.filter((v) => v.component === 'header'),
          ...s.variableFields.map((f, i) => ({ component: 'body', index: i + 1, field: f, sample: samples.get(f) ?? '' })),
        ],
        buttons: s.buttons.map((b) => {
          const link = fields.find((f) => f.key === b.field);
          const base = link?.sample ? link.sample.replace(/[^/]*$/, '') : 'https://ccms.actschurch.co.za/';
          return b.type === 'URL' ? { ...b, url: `${base}{{1}}`, sample: link?.sample ?? null } : b;
        }),
      }));
      setAiNotes(s.notes ?? []);
    } catch (err) {
      setAiError(whatsAppErrorText(err, 'The AI could not suggest wording. Try again shortly.'));
    } finally {
      setAiBusy(false);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const r = await service.submit({ draft, linkToMessage: linkToMessage && !!draft.templateId, replaceExistingLink: replaceLink });
      await qc.invalidateQueries({ queryKey: whatsAppTemplateKeys.all });
      setSubmitted(r.message);
      toast.success('Submitted to Meta', r.message);
    } catch (err) {
      setSubmitError(whatsAppErrorText(err, 'Submitting failed.'));
    } finally {
      setSubmitting(false);
    }
  };

  const bodyVariables = [...new Set(variablesOf(draft.bodyText).filter((v) => v.number !== null).map((v) => v.number as number))].sort((a, b) => a - b);
  const headerHasVariable = variablesOf(draft.headerText).length > 0;
  const nameInvalid = !!draft.name && !isValidName(draft.name);

  const stepErrors = [
    0,
    errorsFor('category').length,
    errorsFor('name', 'language').length,
    errorsFor('header', 'body', 'footer', 'variables', 'buttons').length,
    check.errors.length,
  ];

  const fieldChips = (
    <Group gap={6} wrap="wrap" data-help-id={`${PAGE}.fields`}>
      {fields.length === 0 ? (
        <Text size="xs" c="dimmed">
          Pick a message in step 1 to get its fields here.
        </Text>
      ) : (
        fields
          .filter((f) => !f.isLink)
          .map((f) => {
            const used = draft.variables.find((v) => v.component === 'body' && v.field === f.key);
            return (
              <Tooltip key={f.key} label={`${f.key}${f.sample ? `: e.g. ${f.sample}` : ''}`} withArrow>
                <Chip checked={!!used} onChange={() => insertField(f)} size="xs" variant="light">
                  {used ? `{{${used.index}}} ` : '+ '}
                  {f.label}
                </Chip>
              </Tooltip>
            );
          })
      )}
    </Group>
  );

  return (
    <LayoutContainer py="xl">
      <FadeIn delay={60}>
        <Stack gap="lg">
          <Group justify="space-between" wrap="wrap" align="flex-start" gap="sm">
            <div style={{ minWidth: 0, flex: 1 }}>
              <Button variant="subtle" size="xs" px={4} leftSection={<IconArrowLeft size={16} />} onClick={() => navigate(BACK)}>
                WhatsApp templates
              </Button>
              <Title order={2}>New WhatsApp template</Title>
              <Text size="sm" c="dimmed">
                Write a template step by step. Meta reviews it (usually within minutes, sometimes up to 48 hours) and it is used for its
                message once approved.
              </Text>
            </div>
            <Group gap="sm" data-help-id={`${PAGE}.actions`}>
              <Badge size="lg" variant="light" color={check.ok ? 'green' : 'red'} leftSection={check.ok ? <IconCircleCheck size={14} /> : <IconAlertTriangle size={14} />}>
                {check.ok ? 'Ready to submit' : `${check.errors.length} to fix`}
              </Badge>
              <PageHelpButton pageKey={PAGE} />
            </Group>
          </Group>

          <SimpleGrid cols={{ base: 1, lg: 3 }} spacing="lg">
            <Stack gap="md" style={{ gridColumn: 'span 2' }}>
              <Stepper active={step} onStepClick={setStep} size="sm" allowNextStepsSelect data-help-id={`${PAGE}.steps`}>
                {['Message', 'Category', 'Name', 'Wording', 'Check and submit'].map((label, i) => (
                  <Stepper.Step key={label} label={label} color={stepErrors[i] > 0 && i < 4 && step > i ? 'red' : undefined} />
                ))}
              </Stepper>

              <Paper withBorder radius="md" p="lg">
                {step === 0 && (
                  <Stack gap="md" data-help-id={`${PAGE}.message`}>
                    <Text fw={600}>Which message is this template for?</Text>
                    <Select
                      label="CCMS message"
                      description="Its fields become chips you can put in the wording, and the template links itself to it once approved."
                      placeholder={messages.isLoading ? 'Loading messages' : 'Search messages'}
                      searchable
                      clearable
                      data={all.map((m) => ({
                        value: String(m.templateId),
                        label: `${m.moduleName}: ${m.title} (${m.templateId})`,
                        disabled: m.isSecurity || !m.whatsAppAllowed,
                      }))}
                      value={draft.templateId ? String(draft.templateId) : null}
                      onChange={(v) => pickMessage(v ? Number(v) : null)}
                    />
                    {message && (
                      <Card withBorder radius="md" p="sm">
                        <Text fw={600}>{message.title}</Text>
                        {message.description && <Text size="sm">{message.description}</Text>}
                        {message.audience && (
                          <Text size="xs" c="dimmed">
                            Goes to: {message.audience}
                          </Text>
                        )}
                        {message.link && (
                          <Alert mt="xs" color="yellow" icon={<IconAlertTriangle size={16} />}>
                            Already linked to {message.link.metaTemplateName}. A new template only replaces it if you ask for that when you submit.
                          </Alert>
                        )}
                        <Text size="xs" fw={600} mt="sm" mb={4}>
                          Fields
                        </Text>
                        <Group gap={6}>
                          {message.fields.map((f) => (
                            <Badge key={f.key} variant="light" color={f.isLink ? 'teal' : 'gray'} size="sm">
                              {f.label}
                            </Badge>
                          ))}
                        </Group>
                      </Card>
                    )}
                    <Text size="xs" c="dimmed">
                      No message? Leave it empty to write a general template; link it to a message later.
                    </Text>
                  </Stack>
                )}

                {step === 1 && (
                  <Stack gap="md" data-help-id={`${PAGE}.category`}>
                    <Text fw={600}>What kind of message is it?</Text>
                    <SimpleGrid cols={{ base: 1, md: 3 }} spacing="sm">
                      {CATEGORIES.map((c) => (
                        <Card
                          key={c.code}
                          withBorder
                          radius="md"
                          p="md"
                          onClick={() => !c.disabled && set({ category: c.code })}
                          style={{
                            cursor: c.disabled ? 'not-allowed' : 'pointer',
                            opacity: c.disabled ? 0.6 : 1,
                            borderColor: draft.category === c.code ? `var(--mantine-color-${c.color}-6)` : undefined,
                            borderWidth: draft.category === c.code ? 2 : 1,
                          }}
                        >
                          <Group justify="space-between" mb={6}>
                            <Badge color={c.color} variant="light">
                              {c.name}
                            </Badge>
                            {c.disabled && <IconLock size={16} />}
                          </Group>
                          <Text size="sm">{c.when}</Text>
                          <Text size="xs" c="dimmed" mt={6}>
                            {c.note}
                          </Text>
                        </Card>
                      ))}
                    </SimpleGrid>
                    {errorsFor('category').map((e) => (
                      <Alert key={e.code} color="red">
                        {e.message}
                      </Alert>
                    ))}
                  </Stack>
                )}

                {step === 2 && (
                  <Stack gap="md" data-help-id={`${PAGE}.name`}>
                    <TextInput
                      label="Template name"
                      description="Lower case letters, digits and underscores. People never see it; it cannot change after you submit."
                      value={draft.name}
                      onChange={(e) => {
                        setNameTouched(true);
                        set({ name: e.currentTarget.value });
                      }}
                      error={nameInvalid ? `Not valid. Use ${normaliseName(draft.name)}` : undefined}
                      rightSection={
                        nameInvalid ? (
                          <Tooltip label="Fix the name">
                            <ActionIcon variant="subtle" onClick={() => set({ name: normaliseName(draft.name) })} aria-label="Fix the name">
                              <IconWand size={16} />
                            </ActionIcon>
                          </Tooltip>
                        ) : undefined
                      }
                      ff="monospace"
                    />
                    <Select label="Language" data={LANGUAGES} value={draft.languageCode} onChange={(v) => set({ languageCode: v ?? 'en' })} allowDeselect={false} />
                  </Stack>
                )}

                {step === 3 && (
                  <Stack gap="md" data-help-id={`${PAGE}.wording`}>
                    <Group justify="space-between" wrap="wrap" gap="xs">
                      <Text fw={600}>The wording</Text>
                      <Group gap="xs">
                        {message?.starter && (
                          <Button variant="light" size="xs" leftSection={<IconWand size={14} />} onClick={applyStarter} data-help-id={`${PAGE}.use-suggested`}>
                            Use suggested wording
                          </Button>
                        )}
                        {ai.data?.available && (
                          <Button variant="light" color="violet" size="xs" leftSection={<IconSparkles size={14} />} onClick={suggest} loading={aiBusy} data-help-id={`${PAGE}.ai`}>
                            Suggest wording
                          </Button>
                        )}
                      </Group>
                    </Group>

                    {ai.data && !ai.data.available && isAdmin && (
                      <Alert color="gray" icon={<IconSparkles size={16} />} title="Suggest wording is off">
                        {ai.data.message ?? 'Switch on the WhatsAppTemplateWording AI feature flag to let Claude suggest wording.'}
                      </Alert>
                    )}
                    {ai.data?.available && ai.data.message && isAdmin && <Alert color="yellow">{ai.data.message}</Alert>}
                    {ai.data?.available && (
                      <TextInput
                        size="xs"
                        placeholder="Optional: what should the suggestion do? (for example, mention the venue)"
                        value={instructions}
                        onChange={(e) => setInstructions(e.currentTarget.value.slice(0, 500))}
                      />
                    )}
                    {aiBusy && (
                      <Group gap="xs">
                        <Loader size="xs" />
                        <Text size="xs" c="dimmed">
                          Claude is writing (no personal details are sent)...
                        </Text>
                      </Group>
                    )}
                    {aiError && <Alert color="red">{aiError}</Alert>}
                    {aiNotes.length > 0 && (
                      <Alert color="violet" title="Notes from the suggestion">
                        {aiNotes.join(' ')}
                      </Alert>
                    )}

                    <TextInput
                      label="Header (optional)"
                      description={`Bold first line, at most ${HEADER_MAX} characters, no emoji.`}
                      value={draft.headerText ?? ''}
                      onChange={(e) => set({ headerText: e.currentTarget.value })}
                      error={errorsFor('header')[0]?.message}
                    />
                    {headerHasVariable && (
                      <SimpleGrid cols={2} spacing="xs">
                        <Select
                          size="xs"
                          label="Header {{1}} is"
                          data={fields.map((f) => ({ value: f.key, label: f.label }))}
                          value={draft.variables.find((v) => v.component === 'header')?.field || null}
                          onChange={(v) => setVariable('header', 1, { field: v ?? '', sample: fields.find((f) => f.key === v)?.sample })}
                        />
                        <TextInput
                          size="xs"
                          label="Sample"
                          value={draft.variables.find((v) => v.component === 'header')?.sample ?? ''}
                          onChange={(e) => setVariable('header', 1, { sample: e.currentTarget.value })}
                        />
                      </SimpleGrid>
                    )}

                    <Stack gap={6}>
                      <Text size="sm" fw={500}>
                        Message
                      </Text>
                      <Text size="xs" c="dimmed">
                        Tap a field to put it where the cursor is; it becomes {'{{1}}'}, {'{{2}}'} and so on, in order.
                      </Text>
                      {fieldChips}
                      <Textarea
                        ref={bodyRef}
                        minRows={5}
                        autosize
                        value={draft.bodyText}
                        onChange={(e) => onBodyChange(e.currentTarget.value)}
                        placeholder="Hi {{1}}, thank you for your {{2}} submission. Your reference is {{3}}."
                        error={errorsFor('body')[0]?.message}
                        aria-label="Message"
                      />
                      <Group justify="space-between">
                        <Text size="xs" c={draft.bodyText.length > BODY_MAX ? 'red' : 'dimmed'}>
                          {draft.bodyText.length} / {BODY_MAX}
                        </Text>
                        {check.errors.some((e) => e.code === 'body_variable_gaps' || e.code === 'body_variable_order') && (
                          <Button size="compact-xs" variant="subtle" onClick={() => setDraft(renumber(draft))}>
                            Renumber the fields
                          </Button>
                        )}
                      </Group>
                    </Stack>

                    {bodyVariables.length > 0 && (
                      <Stack gap={6} data-help-id={`${PAGE}.samples`}>
                        <Text size="sm" fw={500}>
                          Fields and samples
                        </Text>
                        <Text size="xs" c="dimmed">
                          Meta reviews the template with these samples. Use realistic, made-up values.
                        </Text>
                        {bodyVariables.map((n) => {
                          const v = draft.variables.find((x) => x.component === 'body' && x.index === n);
                          return (
                            <SimpleGrid key={n} cols={{ base: 1, sm: 3 }} spacing="xs">
                              <Text size="sm" ff="monospace" mt={6}>{`{{${n}}}`}</Text>
                              <Select
                                size="xs"
                                placeholder="Field"
                                data={fields.map((f) => ({ value: f.key, label: f.label }))}
                                value={v?.field || null}
                                onChange={(val) => setVariable('body', n, { field: val ?? '', sample: v?.sample || fields.find((f) => f.key === val)?.sample })}
                                aria-label={`Field for {{${n}}}`}
                              />
                              <TextInput size="xs" placeholder="Sample" value={v?.sample ?? ''} onChange={(e) => setVariable('body', n, { sample: e.currentTarget.value })} aria-label={`Sample for {{${n}}}`} />
                            </SimpleGrid>
                          );
                        })}
                      </Stack>
                    )}

                    <TextInput
                      label="Footer (optional)"
                      description={`Small grey line under the message, at most ${FOOTER_MAX} characters, no fields.`}
                      value={draft.footerText ?? ''}
                      onChange={(e) => set({ footerText: e.currentTarget.value })}
                      error={errorsFor('footer')[0]?.message}
                    />

                    <Divider label="Buttons (optional)" labelPosition="left" />
                    <Stack gap="xs" data-help-id={`${PAGE}.buttons`}>
                      {draft.buttons.map((b, i) => (
                        <Paper key={i} withBorder radius="sm" p="xs">
                          <Group gap="xs" align="flex-end" wrap="wrap">
                            <Badge variant="light" leftSection={<ButtonIcon type={b.type} />}>
                              {b.type === 'URL' ? 'Link' : b.type === 'PHONE_NUMBER' ? 'Call' : 'Quick reply'}
                            </Badge>
                            <TextInput size="xs" label="Label" value={b.text} onChange={(e) => setButton(i, { text: e.currentTarget.value })} w={170} maxLength={BUTTON_TEXT_MAX + 5} />
                            {b.type === 'URL' && (
                              <>
                                <TextInput size="xs" label="Address" value={b.url ?? ''} onChange={(e) => setButton(i, { url: e.currentTarget.value })} style={{ flex: 1, minWidth: 220 }} />
                                <Select
                                  size="xs"
                                  label="Changing part from"
                                  data={fields.filter((f) => f.isLink).map((f) => ({ value: f.key, label: f.label }))}
                                  value={b.field ?? null}
                                  onChange={(v) => setButton(i, { field: v, sample: fields.find((f) => f.key === v)?.sample ?? b.sample })}
                                  w={180}
                                  clearable
                                />
                              </>
                            )}
                            {b.type === 'PHONE_NUMBER' && (
                              <TextInput size="xs" label="Number" value={b.phoneNumber ?? ''} onChange={(e) => setButton(i, { phoneNumber: e.currentTarget.value })} w={170} />
                            )}
                            <ActionIcon variant="subtle" color="red" onClick={() => set({ buttons: draft.buttons.filter((_, j) => j !== i) })} aria-label="Remove button">
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Group>
                          {b.type === 'URL' && variablesOf(b.url).length > 0 && (
                            <TextInput
                              size="xs"
                              mt={6}
                              label="Full sample address"
                              value={b.sample ?? ''}
                              onChange={(e) => setButton(i, { sample: e.currentTarget.value })}
                            />
                          )}
                        </Paper>
                      ))}
                      <Group gap="xs">
                        <Button size="xs" variant="default" leftSection={<IconPlus size={14} />} onClick={() => addButton('URL')} disabled={draft.buttons.filter((b) => b.type === 'URL').length >= 2}>
                          Link
                        </Button>
                        <Button size="xs" variant="default" leftSection={<IconPlus size={14} />} onClick={() => addButton('QUICK_REPLY')}>
                          Quick reply
                        </Button>
                        <Button size="xs" variant="default" leftSection={<IconPlus size={14} />} onClick={() => addButton('PHONE_NUMBER')} disabled={draft.buttons.some((b) => b.type === 'PHONE_NUMBER')}>
                          Call
                        </Button>
                      </Group>
                      {errorsFor('buttons', 'variables').map((e) => (
                        <Text key={e.code + e.message} size="xs" c="red">
                          {e.message}
                        </Text>
                      ))}
                    </Stack>
                  </Stack>
                )}

                {step === 4 && (
                  <Stack gap="md" data-help-id={`${PAGE}.check`}>
                    <Text fw={600}>Check and submit</Text>
                    {check.errors.length === 0 ? (
                      <Alert color="green" icon={<IconCircleCheck size={16} />}>
                        No problems found. Meta still reviews the wording.
                      </Alert>
                    ) : (
                      <Alert color="red" icon={<IconAlertTriangle size={16} />} title={`Fix ${check.errors.length} before submitting`}>
                        <List size="sm" items={check.errors.map((e) => e.message)} />
                      </Alert>
                    )}
                    {check.warnings.length > 0 && (
                      <Alert color="yellow" title="Worth a look">
                        <List size="sm" items={check.warnings.map((w) => w.message)} />
                      </Alert>
                    )}
                    <Card withBorder radius="md" p="sm">
                      <Group gap="lg" wrap="wrap">
                        <Text size="sm">
                          <Text span c="dimmed">Name </Text>
                          <Text span ff="monospace">{draft.name || '(none)'}</Text>
                        </Text>
                        <Text size="sm">
                          <Text span c="dimmed">Category </Text>
                          {CATEGORIES.find((c) => c.code === draft.category)?.name}
                        </Text>
                        <Text size="sm">
                          <Text span c="dimmed">Language </Text>
                          {draft.languageCode}
                        </Text>
                        <Text size="sm">
                          <Text span c="dimmed">For </Text>
                          {message ? message.title : 'no message'}
                        </Text>
                      </Group>
                    </Card>
                    {message && (
                      <Stack gap={4}>
                        <Switch label={`Link it to ${message.title} once Meta approves it`} checked={linkToMessage} onChange={(e) => setLinkToMessage(e.currentTarget.checked)} />
                        {message.link && linkToMessage && (
                          <Switch label={`Replace the current link (${message.link.metaTemplateName})`} checked={replaceLink} onChange={(e) => setReplaceLink(e.currentTarget.checked)} />
                        )}
                      </Stack>
                    )}
                    {submitError && <Alert color="red">{submitError}</Alert>}
                    {submitted ? (
                      <Alert color="green" icon={<IconCircleCheck size={16} />} title="Submitted">
                        {submitted}
                        <Group mt="sm">
                          <Button size="xs" onClick={() => navigate(BACK)}>
                            Back to the templates
                          </Button>
                        </Group>
                      </Alert>
                    ) : (
                      <Group>
                        <Button variant="filled" color="green" leftSection={<IconSend size={16} />} onClick={submit} loading={submitting} disabled={!check.ok} data-help-id={`${PAGE}.submit`}>
                          Submit to Meta
                        </Button>
                      </Group>
                    )}
                  </Stack>
                )}
              </Paper>

              <Group justify="space-between">
                <Button variant="default" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}>
                  Back
                </Button>
                {step < 4 && <Button onClick={() => setStep((s) => Math.min(4, s + 1))}>Next</Button>}
              </Group>
            </Stack>

            <Stack gap="md">
              <Stack gap={6} data-help-id={`${PAGE}.preview`} style={{ position: 'sticky', top: 16 }}>
                <Text size="sm" fw={600}>
                  Preview with the samples
                </Text>
                <WhatsAppPhonePreview parts={preview} />
                <Group gap={6} justify="center">
                  <Badge variant="light" color={check.ok ? 'green' : 'red'}>
                    {check.errors.length} error{check.errors.length === 1 ? '' : 's'}
                  </Badge>
                  <Badge variant="light" color={check.warnings.length ? 'yellow' : 'gray'}>
                    {check.warnings.length} warning{check.warnings.length === 1 ? '' : 's'}
                  </Badge>
                </Group>
              </Stack>
              <GoodMessagePanel />
            </Stack>
          </SimpleGrid>
        </Stack>
      </FadeIn>
    </LayoutContainer>
  );
}
